import React from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import FunctionCombobox from '../rundown/FunctionCombobox';
import { VMIX_FUNCTION_META } from '../../lib/vmixFunctions';
import {
  editRow, cloneRow, makeFunctionRow, makeSleepRow, makeCommentRow, makeRawRow,
  targetLabel,
} from '../../lib/vmixScript';

// A vMix script rendered as a rundown-style action list: one row per step, with
// the same columns a cue action has — target vMix, function, input, value.
//
// Unlike the cue editor's click-to-open form, every field here is live. Mass
// editing is the whole point of this view: retargeting eight calls to a
// different vMix should be eight dropdown changes, not eight open/edit/save
// round trips.

const CELL = 'bg-app-surface border border-app-border rounded px-2 py-1 text-xs text-app-fg focus:outline-none focus:border-blue-500';

export default function ScriptActionsView({ rows, setRows, instances, selected, setSelected }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function update(idx, patch) {
    setRows(rows.map((r, i) => (i === idx ? editRow(r, patch) : r)));
  }

  function insertAfter(idx, row) {
    const next = [...rows];
    next.splice(idx + 1, 0, row);
    setRows(next);
  }

  function remove(idx) {
    setRows(rows.filter((_, i) => i !== idx));
    setSelected(new Set());
  }

  function handleDragEnd(e) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = rows.findIndex(r => r.id === active.id);
    const to = rows.findIndex(r => r.id === over.id);
    if (from === -1 || to === -1) return;
    setRows(arrayMove(rows, from, to));
  }

  function toggleSelect(id, shiftKey, idx) {
    const next = new Set(selected);
    if (shiftKey && next.size > 0) {
      // Extend from the last selected row, so a block of calls can be grabbed
      // in two clicks and retargeted together.
      const lastIdx = rows.findIndex(r => next.has(r.id) && r.id !== id);
      if (lastIdx !== -1) {
        const [a, b] = lastIdx < idx ? [lastIdx, idx] : [idx, lastIdx];
        for (let i = a; i <= b; i++) next.add(rows[i].id);
        setSelected(next);
        return;
      }
    }
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  if (rows.length === 0) {
    return (
      <div className="p-6">
        <p className="text-gray-500 text-sm mb-3">This script is empty.</p>
        <AddRowBar onAdd={row => setRows([row])} />
      </div>
    );
  }

  return (
    <div className="p-3">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={rows.map(r => r.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-0.5">
            {rows.map((row, idx) => (
              <SortableRow
                key={row.id}
                row={row}
                idx={idx}
                instances={instances}
                selected={selected.has(row.id)}
                onToggleSelect={(shift) => toggleSelect(row.id, shift, idx)}
                onChange={patch => update(idx, patch)}
                onDuplicate={() => insertAfter(idx, cloneRow(row))}
                onRemove={() => remove(idx)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="mt-3 pt-3 border-t border-app-border">
        <AddRowBar onAdd={row => setRows([...rows, row])} />
      </div>
    </div>
  );
}

function AddRowBar({ onAdd }) {
  const btn = 'px-2 py-1 text-xs rounded border border-app-border text-gray-400 hover:text-app-fg hover:border-app-border-strong transition-colors';
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-gray-600 uppercase tracking-wide">Add</span>
      <button className={btn} onClick={() => onAdd(makeFunctionRow({ fn: '' }))}>vMix function</button>
      <button className={btn} onClick={() => onAdd(makeSleepRow(1000))}>Sleep</button>
      <button className={btn} onClick={() => onAdd(makeCommentRow(''))}>Comment</button>
      <button className={btn} onClick={() => onAdd(makeRawRow(''))}>Raw VB line</button>
    </div>
  );
}

function SortableRow(props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.row.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style}>
      <Row {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

function Row({ row, idx, instances, selected, onToggleSelect, onChange, onDuplicate, onRemove, dragHandleProps }) {
  return (
    <div
      className={`group flex items-center gap-1.5 rounded px-1.5 py-1 ${
        selected ? 'bg-blue-950/40 ring-1 ring-blue-800' : 'hover:bg-app-row-hover'
      }`}
    >
      <button
        {...dragHandleProps}
        title="Drag to reorder"
        className="w-4 text-gray-700 hover:text-gray-400 cursor-grab active:cursor-grabbing flex-shrink-0"
      >
        ⠿
      </button>

      <input
        type="checkbox"
        checked={selected}
        onChange={e => onToggleSelect(e.nativeEvent.shiftKey)}
        title="Select for bulk edit"
        className="flex-shrink-0 accent-blue-600"
      />

      <span className="w-7 text-right text-[10px] font-mono text-gray-600 flex-shrink-0">{idx + 1}</span>

      <RowBody row={row} instances={instances} onChange={onChange} />

      <button
        onClick={onDuplicate}
        title="Duplicate this step"
        className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-app-fg px-1 flex-shrink-0 transition-opacity"
      >
        ⧉
      </button>
      <button
        onClick={onRemove}
        title="Delete this step"
        className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 px-1 flex-shrink-0 transition-opacity"
      >
        ✕
      </button>
    </div>
  );
}

function RowBody({ row, instances, onChange }) {
  if (row.kind === 'blank') {
    return <span className="flex-1 text-[11px] text-gray-700 italic pl-1">— blank line —</span>;
  }

  if (row.kind === 'comment') {
    return (
      <>
        <KindTag>Comment</KindTag>
        <input
          type="text"
          value={row.text}
          onChange={e => onChange({ text: e.target.value })}
          placeholder="Note to whoever reads this script next"
          className={`flex-1 min-w-0 ${CELL} text-green-500/80 italic`}
        />
      </>
    );
  }

  if (row.kind === 'sleep') {
    return (
      <>
        <KindTag>Sleep</KindTag>
        <input
          type="number"
          min="0"
          step="100"
          value={row.ms}
          onChange={e => onChange({ ms: Math.max(0, parseInt(e.target.value, 10) || 0) })}
          className={`w-24 ${CELL} font-mono`}
        />
        <span className="text-[11px] text-gray-500">ms</span>
        <span className="flex-1" />
      </>
    );
  }

  if (row.kind === 'raw') {
    return (
      <>
        <KindTag title="Script code this editor doesn't model as an action — kept exactly as written">VB</KindTag>
        <input
          type="text"
          value={row.raw}
          // Raw rows are emitted from `raw`, so editing that field IS the edit;
          // pristine must stay true or serializeRow would fall through to it
          // anyway. Set it explicitly so intent is on the page.
          onChange={e => onChange({ raw: e.target.value, pristine: true })}
          spellCheck={false}
          className={`flex-1 min-w-0 ${CELL} font-mono text-amber-500/90`}
        />
      </>
    );
  }

  // kind === 'function'
  const meta = VMIX_FUNCTION_META.get(row.fn);
  const params = meta?.params || '';
  const wantsInput = !params || /Input/i.test(params);
  const wantsValue = !params || /Value/i.test(params);

  return (
    <>
      <select
        value={row.target ? `${row.target.host}:${row.target.port}` : ''}
        onChange={e => {
          const v = e.target.value;
          if (!v) {
            onChange({ target: null, style: row.style === 'webrequest' ? 'named' : row.style });
            return;
          }
          const [host, port] = v.split(':');
          onChange({ target: { host, port: Number(port) }, style: 'webrequest' });
        }}
        title="Which vMix this call is sent to"
        className={`w-40 flex-shrink-0 ${CELL}`}
      >
        <option value="">This vMix</option>
        {instances.map(inst => (
          <option key={inst.id} value={`${inst.host}:${inst.port}`}>{inst.label}</option>
        ))}
        {/* A host in the script that isn't in the instance registry still needs
            to be selectable, or opening this dropdown would silently retarget it. */}
        {row.target && !instances.some(i => i.host === row.target.host && Number(i.port) === Number(row.target.port)) && (
          <option value={`${row.target.host}:${row.target.port}`}>
            {targetLabel(row, instances)} (unregistered)
          </option>
        )}
      </select>

      <FunctionCombobox
        value={row.fn}
        onChange={fn => onChange({ fn })}
        autoFocus={false}
        className="w-56 flex-shrink-0"
        placeholder="Function…"
      />

      <input
        type="text"
        value={row.input}
        onChange={e => onChange({ input: e.target.value })}
        placeholder={wantsInput ? 'Input' : '—'}
        title={wantsInput ? 'Input number, name or key' : `${row.fn} takes no Input`}
        className={`w-24 flex-shrink-0 ${CELL} ${wantsInput ? '' : 'opacity-40'}`}
      />

      <input
        type="text"
        value={row.value}
        onChange={e => onChange({ value: e.target.value })}
        placeholder={wantsValue ? 'Value' : '—'}
        title={meta?.desc || (wantsValue ? 'Value' : `${row.fn} takes no Value`)}
        className={`flex-1 min-w-0 ${CELL} ${wantsValue ? '' : 'opacity-40'}`}
      />

      {row.extras?.length > 0 && (
        <span
          title={row.extras.map(([k, v]) => `${k}="${v}"`).join('  ')}
          className="flex-shrink-0 text-[10px] text-gray-500 border border-app-border rounded px-1 py-0.5"
        >
          +{row.extras.length}
        </span>
      )}
    </>
  );
}

function KindTag({ children, title }) {
  return (
    <span
      title={title}
      className="w-40 flex-shrink-0 text-[10px] uppercase tracking-wide text-gray-600 pl-1"
    >
      {children}
    </span>
  );
}
